import React, { useState, useEffect, useMemo } from "react";
import { Badge, Card, Table, Form, InputGroup, Spinner, Pagination, Collapse, Row, Col, Button } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight, FaFilter, FaTimes } from "react-icons/fa";
import { FaFolderPlus } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Select from "react-select";

import { useAuth } from "../../context/AuthContext";
import { useProjets } from "../../hooks/PROJET/useGetProjets";
import { getProjets } from "../../api/PROJETS/projets";
import { useClients } from "../../hooks/useGetClients";
import { customSelectStyles } from "../../utils/reactSelectStyles";
import ProjetRow from "./ProjetRow";
import NewProject from "../../components/Modals/Create/NewProject";

const MOBILE_BREAKPOINT = 1000;

const ALL_STATUSES = ["open", "in_progress", "closed", "approved"];

const STATUS_LABELS = {
  open:        "Ouvert",
  in_progress: "En cours",
  closed:      "Clôturé",
  approved:    "Approuvé",
};

const STATUS_BADGE = {
  open:        "primary",
  in_progress: "warning",
  closed:      "secondary",
  approved:    "success",
};

const OP_OPTIONS = [
  { value: "=",  label: "=" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
  { value: ">",  label: ">" },
  { value: "<",  label: "<" },
];

const Projets = ({ titre }) => {
  // ── Router ────────────────────────────────────────────────────────────────
  const location      = useLocation();
  const restoredState = location.state;

  // ── Auth ──────────────────────────────────────────────────────────────────
  const { hasPermission } = useAuth();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState(restoredState?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(restoredState?.search || "");
  const [page,            setPage]            = useState(restoredState?.page   || 1);
  const [limit,           setLimit]           = useState(restoredState?.limit  || 10);
  const [showNewProject,  setShowNewProject]  = useState(false);
  const [isMobile,        setIsMobile]        = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [showFilters,     setShowFilters]     = useState(false);
  const [sortField,       setSortField]       = useState("date_debut");
  const [sortOrder,       setSortOrder]       = useState("DESC");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  };

  // ── Filter state ──────────────────────────────────────────────────────────
  const [filterStatuses,    setFilterStatuses]    = useState(new Set(ALL_STATUSES));
  const [filterClient,      setFilterClient]      = useState(null);   // { value: id, label: name }
  const [filterClientInput, setFilterClientInput] = useState("");
  const [filterDateDebut,   setFilterDateDebut]   = useState({ op: ">=", val: "" });
  const [filterDateFin,     setFilterDateFin]     = useState({ op: "<=", val: "" });
  const [filterDateCloture, setFilterDateCloture] = useState({ op: "=",  val: "" });

  // ── Client options ────────────────────────────────────────────────────────
  const { data: clientsData } = useClients(
    1, 20,
    filterClientInput.length >= 3 ? filterClientInput : ""
  );
  const clientOptions = (clientsData?.data?.clients ?? []).map(c => ({ value: c.id, label: c.name }));

  // ── API filters object ────────────────────────────────────────────────────
  const apiFilters = useMemo(() => ({
    statuses:    filterStatuses.size === ALL_STATUSES.length ? [] : [...filterStatuses],
    clientId:    filterClient?.value ?? null,
    dateDebut:   filterDateDebut.val  ? filterDateDebut  : null,
    dateFin:     filterDateFin.val    ? filterDateFin    : null,
    dateCloture: filterDateCloture.val ? filterDateCloture : null,
  }), [filterStatuses, filterClient, filterDateDebut, filterDateFin, filterDateCloture]);

  // Count of active filters (badge on filter button)
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterStatuses.size < ALL_STATUSES.length) n++;
    if (filterClient)          n++;
    if (filterDateDebut.val)   n++;
    if (filterDateFin.val)     n++;
    if (filterDateCloture.val) n++;
    return n;
  }, [filterStatuses, filterClient, filterDateDebut, filterDateFin, filterDateCloture]);

  // ── Remote data ───────────────────────────────────────────────────────────
  const queryClient                  = useQueryClient();
  const { data, isLoading, isError } = useProjets(page, limit, debouncedSearch, apiFilters, sortField, sortOrder);

  // ── Derived values ────────────────────────────────────────────────────────
  const projets    = Array.isArray(data?.data?.projects) ? data.data.projects : [];
  const totalItems = data?.data?.count || 0;
  const totalPages = Math.ceil(totalItems / limit);

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (page < totalPages) {
      queryClient.prefetchQuery({
        queryKey: ["projets", page + 1, limit, debouncedSearch, apiFilters, sortField, sortOrder],
        queryFn:  () => getProjets(page + 1, limit, debouncedSearch, apiFilters, sortField, sortOrder),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [page, limit, debouncedSearch, apiFilters, sortField, sortOrder, totalPages, queryClient]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const toggleStatus = (status) => {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      next.has(status) ? next.delete(status) : next.add(status);
      return next;
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilterStatuses(new Set(ALL_STATUSES));
    setFilterClient(null);
    setFilterClientInput("");
    setFilterDateDebut({ op: ">=", val: "" });
    setFilterDateFin({ op: "<=", val: "" });
    setFilterDateCloture({ op: "=",  val: "" });
    setPage(1);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const delta = 2;
    const left  = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    return [
      1,
      ...(left > 2 ? ["..."] : []),
      ...Array.from({ length: right - left + 1 }, (_, i) => left + i),
      ...(right < total - 1 ? ["..."] : []),
      total,
    ];
  };

  const paginationBar = (keyPrefix) => (
    <Pagination className="mb-0 flex-wrap">
      <Pagination.Prev
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={page === 1}
      >
        {isMobile ? <FaChevronLeft /> : "Précédent"}
      </Pagination.Prev>

      {getPageNumbers(page, totalPages).map((p, idx) =>
        p === "..." ? (
          <Pagination.Ellipsis key={`${keyPrefix}-ellipsis-${idx}`} disabled />
        ) : (
          <Pagination.Item
            key={`${keyPrefix}-page-${p}`}
            active={p === page}
            onClick={() => setPage(p)}
          >
            {p}
          </Pagination.Item>
        )
      )}

      <Pagination.Next
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
      >
        {isMobile ? <FaChevronRight /> : "Suivant"}
      </Pagination.Next>
    </Pagination>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Page header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>

        {hasPermission("CREATE_PROJECT") && (
          <button
            className="btn btn-info btn-icon-split ms-2 mb-0"
            onClick={() => setShowNewProject(true)}
          >
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <FaFolderPlus style={{ color: "#fff" }} />
            </span>
            <span className="text">Créer un projet</span>
          </button>
        )}
      </div>

      {showNewProject && (
        <NewProject showNewProject={showNewProject} setShowNewProject={setShowNewProject} />
      )}

      <div className="row">
        <div className="col-12">
          <Card className="border-left-info shadow mb-4">
            <Card.Body>

              {/* Toolbar: limit selector + search + filter button + top pagination */}
              <div
                className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3"
                style={{ width: "100%" }}
              >
                <div style={{ flex: "1 1 150px", maxWidth: "70px" }}>
                  <Form.Select value={limit} onChange={handleLimitChange} disabled={isLoading}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>

                <div style={{ flex: "2 1 200px", maxWidth: "350px" }}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Recherche par titre"
                      value={search}
                      onChange={handleSearchChange}
                      className="before-input"
                    />
                  </InputGroup>
                </div>

                {/* Filter toggle button */}
                <div>
                  <Button
                    variant={showFilters || activeFilterCount > 0 ? "warning" : "outline-secondary"}
                    className="d-flex align-items-center gap-2 filter-button"
                    onClick={() => setShowFilters(prev => !prev)}
                    title="Filtres avancés"
                  >
                    <FaFilter />
                    <span>Filtres</span>
                    {activeFilterCount > 0 && (
                      <Badge bg="light" text="dark" pill className="ms-1" style={{ display: 'inline-flex', alignItems: 'center', height: 'fit-content' }}>
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </div>

                <div
                  className="d-flex justify-content-end"
                  style={{ flex: "3 1 250px", minWidth: "250px" }}
                >
                  {paginationBar("top")}
                </div>
              </div>

              {/* ── Filter panel (collapse) ─────────────────────────────── */}
              <Collapse in={showFilters}>
                <div>
                  <div className="border rounded p-3 mb-3 bg-body-secondary">

                    {/* Panel header */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="fw-semibold text-muted small text-uppercase">Filtres avancés</span>
                      {activeFilterCount > 0 && (
                        <button className="btn btn-danger btn-icon-split btn-sm" onClick={resetFilters}>
                          <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
                            <FaTimes style={{ color: "#fff" }} />
                          </span>
                          <span className="text">Réinitialiser</span>
                        </button>
                      )}
                    </div>

                    {/* Statut */}
                    <div className="mb-3">
                      <Form.Label className="small fw-semibold mb-1">Statut</Form.Label>
                      <div className="d-flex gap-3 flex-wrap">
                        {ALL_STATUSES.map(status => (
                          <Form.Check
                            key={status}
                            type="checkbox"
                            id={`fp-status-${status}`}
                            checked={filterStatuses.has(status)}
                            onChange={() => toggleStatus(status)}
                            label={
                              <Badge
                                bg={STATUS_BADGE[status]}
                                text={status === "in_progress" ? "dark" : undefined}
                              >
                                {STATUS_LABELS[status]}
                              </Badge>
                            }
                          />
                        ))}
                      </div>
                    </div>

                    {/* Client + Description */}
                    <Row className="mb-3 g-3">
                      <Col md={6}>
                        <Form.Label className="small fw-semibold mb-1">Client</Form.Label>
                        <Select
                          isClearable
                          value={filterClient}
                          onChange={opt => { setFilterClient(opt); setPage(1); }}
                          options={clientOptions}
                          onInputChange={val => setFilterClientInput(val)}
                          inputValue={filterClientInput}
                          placeholder="Rechercher un client..."
                          noOptionsMessage={() =>
                            filterClientInput.length < 3
                              ? "Entrez au moins 3 caractères"
                              : "Aucun client trouvé"
                          }
                          styles={customSelectStyles}
                        />
                      </Col>
                    </Row>

                    {/* Date filters */}
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Date de début</Form.Label>
                        <InputGroup>
                          <Form.Select
                            value={filterDateDebut.op}
                            onChange={e => setFilterDateDebut(prev => ({ ...prev, op: e.target.value }))}
                            style={{ maxWidth: "60px" }}
                          >
                            {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </Form.Select>
                          <Form.Control
                            type="date"
                            value={filterDateDebut.val}
                            onChange={e => { setFilterDateDebut(prev => ({ ...prev, val: e.target.value })); setPage(1); }}
                          />
                          {filterDateDebut.val && (
                            <Button variant="outline-secondary" onClick={() => setFilterDateDebut({ op: ">=", val: "" })}>
                              <FaTimes size={10} />
                            </Button>
                          )}
                        </InputGroup>
                      </Col>

                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Date de fin</Form.Label>
                        <InputGroup>
                          <Form.Select
                            value={filterDateFin.op}
                            onChange={e => setFilterDateFin(prev => ({ ...prev, op: e.target.value }))}
                            style={{ maxWidth: "60px" }}
                          >
                            {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </Form.Select>
                          <Form.Control
                            type="date"
                            value={filterDateFin.val}
                            onChange={e => { setFilterDateFin(prev => ({ ...prev, val: e.target.value })); setPage(1); }}
                          />
                          {filterDateFin.val && (
                            <Button variant="outline-secondary" onClick={() => setFilterDateFin({ op: "<=", val: "" })}>
                              <FaTimes size={10} />
                            </Button>
                          )}
                        </InputGroup>
                      </Col>

                      <Col md={4}>
                        <Form.Label className="small fw-semibold mb-1">Date de clôture</Form.Label>
                        <InputGroup>
                          <Form.Select
                            value={filterDateCloture.op}
                            onChange={e => setFilterDateCloture(prev => ({ ...prev, op: e.target.value }))}
                            style={{ maxWidth: "60px" }}
                          >
                            {OP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </Form.Select>
                          <Form.Control
                            type="date"
                            value={filterDateCloture.val}
                            onChange={e => { setFilterDateCloture(prev => ({ ...prev, val: e.target.value })); setPage(1); }}
                          />
                          {filterDateCloture.val && (
                            <Button variant="outline-secondary" onClick={() => setFilterDateCloture({ op: "=", val: "" })}>
                              <FaTimes size={10} />
                            </Button>
                          )}
                        </InputGroup>
                      </Col>
                    </Row>

                  </div>
                </div>
              </Collapse>

              {/* Content area */}
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status" variant="info">
                    <span className="visually-hidden">Chargement...</span>
                  </Spinner>
                </div>

              ) : isError ? (
                <div className="alert alert-danger">
                  Erreur lors du chargement des projets
                </div>

              ) : (
                <>
                  {isMobile ? (
                    <div className="row g-3">
                      {projets.length === 0 ? (
                        <div className="col-12">
                          <div className="alert alert-info text-center mb-0">Aucun projet trouvé</div>
                        </div>
                      ) : (
                        projets.map((projet, idx) => (
                          <div key={projet.id ?? `projet-${idx}`} className="col-12">
                            <ProjetRow projet={projet} listState={{ page, limit, search }} asCard />
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <Table striped hover responsive className="align-middle">
                      <thead>
                        <tr>
                          {[
                            { label: "ID",         field: "id" },
                            { label: "Titre",      field: "titre" },
                            { label: "Client",     field: "client" },
                            { label: "Statut",     field: "status" },
                            { label: "Date début", field: "date_debut" },
                            { label: "Date fin",   field: "date_fin" },
                          ].map(({ label, field }) => (
                            <th
                              key={field}
                              onClick={() => handleSort(field)}
                              style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                            >
                              {label}{" "}
                              {sortField === field
                                ? (sortOrder === "ASC" ? "▲" : "▼")
                                : <span style={{ opacity: 0.25 }}>▲</span>}
                            </th>
                          ))}
                          <th>Action(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projets.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center">Aucun projet trouvé</td>
                          </tr>
                        ) : (
                          projets.map((projet, idx) => (
                            <ProjetRow
                              key={projet.id ?? `projet-${idx}`}
                              projet={projet}
                              listState={{ page, limit, search }}
                            />
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}

                  {/* Bottom pagination + total count */}
                  <div className="d-flex flex-column gap-3 mt-3">
                    {paginationBar("bottom")}
                    <div className="text-muted fst-italic">Total : {totalItems} projet{totalItems !== 1 ? "s" : ""}</div>
                  </div>
                </>
              )}

            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Projets;
